import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { Grid } from '@/common/grid/Grid';
import { useDependentKey } from '@/common/hooks/key/useDependentKey';
import { useNavState } from '@/common/navState/NavStateContext';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

export const McoFinTrendChart = ({ formValues, isPaid, isChart, idToProgType }) => {
  const { rbtPeriodId, pbmIds, ctrtIds, brandProdIds, progTypeIds } = formValues;
  const rsrc = useRsrc();
  const gridRef = useRef();

  const { navigateTo } = useNavState();

  const [data, setData] = useState([]);

  const [fieldDefs, setFieldDefs] = useState();
  const [isGridReady, setIsGridReady] = useState(false);

  const [prodIds, setProdIds] = useState([]);
  const [progIds, setProgIds] = useState([]);

  const theme = useTheme();

  useEffect(() => {
    if (isChart) {
      return;
    }

    const reqParam = {
      periodId: rbtPeriodId,
    };

    rsrc.data('sc', 'MCD_FIN_TREND_FIELD_DEFS_DATA_PLUGIN', reqParam).then((fieldDefs) => {
      setFieldDefs(fieldDefs);
    });
  }, [isChart, rbtPeriodId]);

  useEffect(() => {
    const reqParam = {
      periodId: rbtPeriodId,
      pbmIds,
      ctrtIds,
      brandIds: brandProdIds,
      progTypes: progTypeIds.map((id) => idToProgType[id]),
      isPaid,
      isChart,
    };

    rsrc.data('sc', 'MCO_FIN_TREND_CHART_DATA_PLUGIN', reqParam).then((data) => {
      if (isChart) {
        data.series[0].color = isPaid ? theme.palette.success.main : theme.palette.error.main;
        setProdIds(data.prodIds);
        setProgIds(data.progIds);
      }
      setData(data);
    });
  }, [rbtPeriodId, pbmIds, ctrtIds, brandProdIds, progTypeIds, isChart]);

  const onGridReady = () => {
    setIsGridReady(true);
  };

  useEffect(() => {
    if (isGridReady && gridRef.current && data) {
      gridRef.current.setDataSource(data.rows);
      gridRef.current.setTotalsRow(data.totalsRow);
    }
  }, [isGridReady, data]);

  const navigateToLedgerLineList = (dataPoint) => {
    const rbtPeriodCode = dataPoint.category;
    const rowsReq = {};
    rowsReq.expr = ExprUtils.whereParams('code', rbtPeriodCode);
    rsrc.one('sc', 'PERIOD', rowsReq).then((period) => {
      const rbtPeriodId = period.id;
      let replaceParams = {};
      replaceParams['rbtPeriodId'] = rbtPeriodId;

      if (prodIds.length > 0) {
        replaceParams['prodIds'] = prodIds;
      }

      if (pbmIds.length > 0) {
        replaceParams['pbmIds'] = pbmIds;
      }

      if (progIds.length > 0) {
        replaceParams['progIds'] = progIds;
      }

      if (progTypeIds.length > 0) {
        replaceParams['progTypes'] = progTypeIds.map((id) => idToProgType[id]);
      }

      replaceParams['statuses'] = isPaid ? ['PAID'] : ['PAID', 'DRAFT'];

      const navState = {
        mode: Mode.LIST,
        //expr: expr,
        replaceParams: replaceParams,
        query: 'MCO_FIN_TREND_LEDGER_LINES',
      };
      navigateTo('/sc/mco-ledger-line', navState);
    });
  };

  const gridKey = useDependentKey(fieldDefs);

  return (
    <>
      {isChart && (
        <CartesianChart
          data={data}
          yLabel={isPaid ? 'Paid Dollars' : 'Disputed Dollars'}
          xLabel="Rebate Period"
          allowNegative
          onClick={(dataPoint) => navigateToLedgerLineList(dataPoint)}
        />
      )}

      {!isChart && fieldDefs && (
        <Grid
          key={gridKey}
          ref={gridRef}
          gridOptions={{
            fieldDefs,
            rowModelType: 'clientSide',
            statusBar: null,
          }}
          events={{ onGridReady }}
        />
      )}
    </>
  );
};
